import React, { useEffect, useState } from 'react';

// Panel de configuración "Gestión Salud" (tab del modal Gestionar cliente).
// - Prende/apaga el flag is_gestion_salud (al prender, el backend siembra defaults).
// - Módulos de riesgo (risk/amef/cause): activar + renombrar.
// - Categorías (Certificación de Calidad / Normatividad): renombrar + activar.
// - Items (normas) por categoría: crear/renombrar/activar/borrar + type_code.

const token = () => document.querySelector('[name="csrf-token"]')?.content;

function api(url, method, body) {
  return fetch(url, {
    method,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token(), Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  }).then(r => r.json().then(j => ({ ok: r.ok, j })));
}

export default function ClientSaludPanel({ clientId }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  const base = `/settings/clients_v2/${clientId}`;

  const load = () => {
    setLoading(true);
    api(`${base}/gestion_salud`, 'GET')
      .then(({ ok, j }) => { if (ok) setCfg(j); })
      .finally(() => setLoading(false));
  };

  useEffect(load, [clientId]);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 2500); };

  const apply = (promise, okText) => promise.then(({ ok, j }) => {
    if (ok) { setCfg(j); if (okText) flash('ok', okText); }
    else flash('err', (j && j.error) || 'No se pudo guardar.');
    return ok;
  });

  const toggleSalud = (enabled) =>
    apply(api(`${base}/gestion_salud`, 'PATCH', { is_gestion_salud: enabled }),
      enabled ? 'Gestión Salud activada.' : 'Gestión Salud desactivada.');

  if (loading) return <div className="cv2-loading"><i className="fas fa-spinner fa-spin" /> Cargando…</div>;
  if (!cfg) return <div className="cv2-error">No se pudo cargar la configuración.</div>;

  return (
    <div className="cv2-salud">
      <h4 className="cv2-panel-title">Gestión Salud</h4>
      <p className="cv2-salud__hint">
        Activa Gestión Salud para este cliente y configura qué módulos y normas ve, con sus nombres.
      </p>

      <label className="cv2-salud__switch">
        <input type="checkbox" checked={!!cfg.is_gestion_salud} onChange={e => toggleSalud(e.target.checked)} />
        <span><strong>Es Gestión Salud</strong> — habilita el módulo de calidad/normatividad y riesgos.</span>
      </label>

      {msg && <div className={`cv2-salud__msg cv2-salud__msg--${msg.type}`}>{msg.text}</div>}

      {cfg.is_gestion_salud && (
        <>
          <ModulesSection cfg={cfg} base={base} apply={apply} />
          <CategorySection cfg={cfg} base={base} apply={apply} kind="certificacion" />
          <CategorySection cfg={cfg} base={base} apply={apply} kind="normatividad" />
        </>
      )}
    </div>
  );
}

/* ─────────── Módulos de riesgo ─────────── */
function ModulesSection({ cfg, base, apply }) {
  return (
    <div className="cv2-salud__section">
      <h5 className="cv2-salud__section-title"><i className="fas fa-toolbox" /> Módulos de riesgo</h5>
      <div className="cv2-salud__list">
        {cfg.modules.map(m => <ModuleRow key={m.id} m={m} base={base} apply={apply} />)}
      </div>
    </div>
  );
}

function ModuleRow({ m, base, apply }) {
  const [name, setName] = useState(m.name);
  const dirty = name !== m.name;
  return (
    <div className="cv2-salud__row">
      <label className="cv2-salud__row-check">
        <input type="checkbox" checked={!!m.active}
          onChange={e => apply(api(`${base}/gs_modules/${m.id}`, 'PATCH', { active: e.target.checked }))} />
      </label>
      <input className="cv2-salud__input" value={name} onChange={e => setName(e.target.value)} />
      <button className="cv2-btn cv2-btn--sm" disabled={!dirty}
        onClick={() => apply(api(`${base}/gs_modules/${m.id}`, 'PATCH', { name }), 'Guardado.')}>
        Guardar
      </button>
    </div>
  );
}

/* ─────────── Categoría + sus items ─────────── */
function CategorySection({ cfg, base, apply, kind }) {
  const category = cfg.categories.find(c => c.kind === kind);
  const items = cfg.items.filter(i => i.kind === kind);
  const [catName, setCatName] = useState(category ? category.name : '');
  const [adding, setAdding] = useState(false);
  const [newItem, setNewItem] = useState({ type_code: '', name: '' });

  if (!category) return null;
  const catDirty = catName !== category.name;

  const createItem = () => {
    if (!newItem.type_code.trim() || !newItem.name.trim()) return;
    apply(api(`${base}/gs_items`, 'POST', { kind, type_code: newItem.type_code, name: newItem.name }), 'Norma agregada.')
      .then(ok => { if (ok) { setNewItem({ type_code: '', name: '' }); setAdding(false); } });
  };

  return (
    <div className="cv2-salud__section">
      <div className="cv2-salud__cat-head">
        <label className="cv2-salud__row-check" title="Activar categoría">
          <input type="checkbox" checked={!!category.active}
            onChange={e => apply(api(`${base}/gs_categories/${category.id}`, 'PATCH', { active: e.target.checked }))} />
        </label>
        <input className="cv2-salud__input cv2-salud__input--cat" value={catName} onChange={e => setCatName(e.target.value)} />
        <button className="cv2-btn cv2-btn--sm" disabled={!catDirty}
          onClick={() => apply(api(`${base}/gs_categories/${category.id}`, 'PATCH', { name: catName }), 'Categoría guardada.')}>
          Guardar
        </button>
      </div>

      <div className="cv2-salud__items">
        <div className="cv2-salud__items-head">
          <span style={{ width: 34 }} />
          <span style={{ width: 120 }}>Código</span>
          <span style={{ flex: 1 }}>Nombre</span>
          <span style={{ width: 120 }} />
        </div>
        {items.map(it => <ItemRow key={it.id} it={it} base={base} apply={apply} />)}

        {adding ? (
          <div className="cv2-salud__row">
            <span style={{ width: 34 }} />
            <input className="cv2-salud__input" style={{ width: 120, textTransform: 'uppercase' }}
              placeholder="PCH" value={newItem.type_code}
              onChange={e => setNewItem(n => ({ ...n, type_code: e.target.value }))} />
            <input className="cv2-salud__input" placeholder="Nombre de la norma"
              value={newItem.name} onChange={e => setNewItem(n => ({ ...n, name: e.target.value }))} />
            <button className="cv2-btn cv2-btn--sm cv2-btn--primary" onClick={createItem}>Crear</button>
            <button className="cv2-btn cv2-btn--sm" onClick={() => setAdding(false)}>×</button>
          </div>
        ) : (
          <button className="cv2-salud__add" onClick={() => setAdding(true)}>
            <i className="fas fa-plus" /> Agregar norma
          </button>
        )}
      </div>
    </div>
  );
}

function ItemRow({ it, base, apply }) {
  const [code, setCode] = useState(it.type_code);
  const [name, setName] = useState(it.name);
  const dirty = code !== it.type_code || name !== it.name;

  const del = () => {
    if (!window.confirm(`¿Eliminar la norma "${it.name}"?`)) return;
    apply(api(`${base}/gs_items/${it.id}`, 'DELETE'), 'Norma eliminada.');
  };

  return (
    <div className="cv2-salud__row">
      <label className="cv2-salud__row-check">
        <input type="checkbox" checked={!!it.active}
          onChange={e => apply(api(`${base}/gs_items/${it.id}`, 'PATCH', { active: e.target.checked }))} />
      </label>
      <input className="cv2-salud__input" style={{ width: 120, textTransform: 'uppercase' }}
        value={code} onChange={e => setCode(e.target.value)} />
      <input className="cv2-salud__input" value={name} onChange={e => setName(e.target.value)} />
      <button className="cv2-btn cv2-btn--sm" disabled={!dirty}
        onClick={() => apply(api(`${base}/gs_items/${it.id}`, 'PATCH', { type_code: code, name }), 'Guardado.')}>
        Guardar
      </button>
      <button className="cv2-btn cv2-btn--sm cv2-btn--danger" onClick={del} title="Eliminar">
        <i className="fas fa-trash" />
      </button>
    </div>
  );
}
